import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import type { WaterBillTarget } from "../../models/WaterBilling";
import { TableBody, TableCell, TableRow, Typography } from "@mui/material";
import type { BillPeriod } from "../../../../shared/utils/billPeriods";
import {
	applyTrackedGallons,
	recalculateBillTotals,
	WaterBillGridRow,
} from "../../utils/billRows";
import { BillRows } from "./BillRows";
import { useBillEditing } from "./hooks/useBillEditing";
import { useTrackedGallonsPreview } from "./hooks/useTrackedGallonsPreview";

const groupByBill = (rows: WaterBillGridRow[]) => {
	const groups: WaterBillGridRow[][] = [];
	for (const row of rows) {
		const last = groups[groups.length - 1];
		if (last && last[0].billId === row.billId) last.push(row);
		else groups.push([row]);
	}
	return groups;
};

type CustomTableBodyProps = {
	rows: WaterBillGridRow[];
	draftRows: WaterBillGridRow[];
	targets: WaterBillTarget[];
	editingBillId: number | null;
	setEditingBillId: (billId: number | null) => void;
	setDraftRows: React.Dispatch<React.SetStateAction<WaterBillGridRow[]>>;
	periods: BillPeriod[];
	isSaving: boolean;
	onBillSave: (rows: WaterBillGridRow[]) => void;
	onBillDelete: (billId: number) => void;
};

export const CustomTableBody = ({
	rows,
	draftRows,
	targets,
	editingBillId,
	setEditingBillId,
	setDraftRows,
	periods,
	isSaving,
	onBillSave,
	onBillDelete,
}: CustomTableBodyProps) => {
	const editing = useBillEditing({
		rows,
		draftRows,
		setDraftRows,
		targetCodes: targets.map((allocation) => allocation.TargetCode),
		periods,
		editingBillId,
		setEditingBillId,
		onBillSave,
	});

	const editedHeader =
		editingBillId === null
			? null
			: (draftRows.find(
					(row) => row.billId === editingBillId && row.isFirstOfBill,
				) ?? null);

	const { tracked, isLoading: isTrackedLoading } = useTrackedGallonsPreview({
		billId: editingBillId,
		start: editedHeader?.servicePeriodStart ?? null,
		end: editedHeader?.servicePeriodEnd ?? null,
		enabled: editingBillId !== null && editing.isPeriodDirty(editingBillId),
	});

	const recalculatedRows =
		editingBillId === null
			? draftRows
			: recalculateBillTotals(draftRows, editingBillId);
	const visibleRows =
		editingBillId === null || tracked === undefined
			? recalculatedRows
			: applyTrackedGallons(recalculatedRows, editingBillId, tracked);

	if (draftRows.length === 0)
		return (
			<TableBody>
				<TableRow>
					<TableCell colSpan={15 + targets.length * 3} sx={{ border: 0 }}>
						<Typography
							sx={{
								color: "text.secondary",
								fontSize: "0.85rem",
								py: 6,
								textAlign: "center",
							}}
						>
							No bills entered for this account yet.
						</Typography>
					</TableCell>
				</TableRow>
			</TableBody>
		);

	return (
		<>
			{groupByBill(visibleRows).map((billRows) => (
				<BillRows
					key={billRows[0].billId}
					rows={billRows}
					targets={targets}
					periods={periods}
					editing={editing}
					isSaving={isSaving}
					isTrackedLoading={isTrackedLoading}
					isTrackedFresh={tracked !== undefined}
					onBillDelete={onBillDelete}
				/>
			))}
			<ConfirmationDialogRaw
				title="Discard changes"
				message="This bill has unsaved changes. Discard them?"
				open={editing.isConfirmingDiscard}
				buttons={{ no: "Keep editing", yes: "Discard" }}
				onClose={(value) => editing.resolvePending(value === "OK")}
			/>
		</>
	);
};
